/**
 * Player2 - Main Entry Point
 * 1MinuteFunnel Quiz Player
 */

import '../css/main.css';

import { initializeLogger, createLogger } from './core/logger';
import { initConfig, getModeConfiguration, getFunnelName, shouldReset } from './core/config';
import {
  setOperatingMode,
  setFunnel,
  setFunnelError,
  setLoading,
  setShowModeBanner,
  loadProgress,
  clearProgress,
  setProgressSaveFunction,
  getState
} from './core/state';
import { loadFunnel, saveProgressToServer, saveSession, createSessionData } from './services/api';
import { initializePixels } from './services/pixels';
import { getAllTrackingData, initEngagementTracking } from './services/tracking';
import { setUTMParams, setCustomParams } from './core/state';
import { initQuizRenderer, renderQuiz } from './components/QuizRenderer';
import { initContactForm, renderContactForm } from './components/ContactForm';
import { renderModeBanner } from './components/ModeBanner';
import { shouldShowAgeGate, showAgeGate } from './components/AgeGate';
import { shouldShowGDPRConsent, showGDPRConsentModal } from './components/GDPRConsent';
import { preloadFunnelAssets, preconnectToDomain } from './services/preload';
import { initEmbed, sendComplete, sendResize } from './services/embed';

const logger = createLogger('App');

/**
 * Main app initialization
 */
async function init(): Promise<void> {
  // Initialize logger first
  initializeLogger();
  logger.info('Player2 initializing...');

  // Start engagement tracking (tab switches, time on page)
  initEngagementTracking();

  // Wire up server-side progress saving
  setProgressSaveFunction(saveProgressToServer);

  // Listen for Cmd+Shift+R / Ctrl+Shift+R to reset quiz
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'r' || e.key === 'R')) {
      e.preventDefault(); // Prevent browser's hard refresh
      clearProgress();
      logger.info('Progress cleared via keyboard shortcut, reloading...');
      // Navigate to reset URL - preserve existing params, just add reset=true
      const url = new URL(window.location.href);
      url.searchParams.set('reset', 'true');
      window.location.href = url.toString();
    }
  });

  // Initialize config
  const config = initConfig();
  setOperatingMode(config.operatingMode);

  // Initialize embed communicator (checks if running in iframe)
  initEmbed();

  // Check for reset - explicit param OR auto-reset in test/inprogress modes
  // This makes development easier - add ?noreset=true to preserve progress in test mode
  const autoResetInDevMode = (config.operatingMode !== 'production') && !config.urlParams.get('noreset');

  if (shouldReset() || autoResetInDevMode) {
    clearProgress();
    logger.info('Progress reset', {
      explicit: shouldReset(),
      autoReset: autoResetInDevMode,
      mode: config.operatingMode
    });

    // Clean up URL - remove reset param so normal refresh doesn't keep resetting
    if (shouldReset()) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('reset');
      window.history.replaceState({}, '', cleanUrl.toString());
    }
  }

  // Extract tracking data
  const trackingData = getAllTrackingData();
  setUTMParams(trackingData.utmParams);
  setCustomParams(trackingData.customParams);

  // Get app container
  const appContainer = document.getElementById('app');
  if (!appContainer) {
    logger.error('App container not found');
    return;
  }

  // Show loading
  renderLoading(appContainer);

  // Load funnel
  const funnelName = getFunnelName();

  // Preconnect to funnel domain for faster API/asset requests
  if (funnelName) {
    preconnectToDomain(`https://${funnelName}.1minutefunnel.com`);
  }
  if (!funnelName) {
    renderError(appContainer, 'No Funnel Found', 'Could not determine which funnel to load.');
    // Save error session for tracking
    const sessionId = getState().session.sessionId;
    const sessionData = createSessionData(sessionId, 'unknown', 0, null, {
      code: 'NO_FUNNEL',
      message: 'Could not determine which funnel to load'
    });
    saveSession(sessionData);
    return;
  }

  // Track funnel load time
  const loadStartTime = Date.now();
  const result = await loadFunnel();
  const funnelLoadTime = Date.now() - loadStartTime;

  if (!result.success || !result.funnel) {
    setFunnelError(result.error?.message || 'Failed to load funnel');
    renderError(
      appContainer,
      result.error?.code === 'NOT_FOUND' ? 'Funnel Not Found' : 'Error Loading Funnel',
      result.error?.message || 'An error occurred while loading the funnel.'
    );
    // Save error session for tracking
    const sessionId = getState().session.sessionId;
    const sessionData = createSessionData(sessionId, funnelName, 0, funnelLoadTime, {
      code: result.error?.code || 'UNKNOWN',
      message: result.error?.message || 'Failed to load funnel'
    });
    saveSession(sessionData);
    return;
  }

  // Store funnel in state
  setFunnel(result.funnel);
  setLoading(false);

  // Save session (page view) for ad tracking
  const sessionId = getState().session.sessionId;
  const sessionData = createSessionData(
    sessionId,
    result.funnel.funnelID,
    result.funnel.version,
    funnelLoadTime,
    null // no error
  );
  saveSession(sessionData); // Fire and forget

  // Preload critical assets (images, etc.) for better LCP
  preloadFunnelAssets(result.funnel);

  // Apply theme
  applyTheme(result.funnel.theme);

  // Initialize pixels (only fires in production)
  if (result.funnel.tracking) {
    initializePixels(result.funnel.tracking);
  }

  // Show mode banner if not production
  const modeConfig = getModeConfiguration();
  if (modeConfig.showModeBanner) {
    setShowModeBanner(true);
  }

  // Load saved progress
  loadProgress(result.funnel.funnelID);

  // Render the app
  renderApp(appContainer, result.funnel, modeConfig);
}

/**
 * Render loading screen
 */
function renderLoading(container: HTMLElement): void {
  container.innerHTML = `
    <div class="loading-screen">
      <div class="loading-spinner"></div>
    </div>
  `;
}

/**
 * Render error screen
 */
function renderError(container: HTMLElement, title: string, message: string): void {
  container.innerHTML = `
    <div class="error-screen">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <h1 class="error-title">${title}</h1>
        <p class="error-message">${message}</p>
        <button class="btn btn-primary" onclick="location.reload()">Try Again</button>
      </div>
    </div>
  `;
}

/**
 * Render the main app
 */
async function renderApp(
  container: HTMLElement,
  funnel: typeof loadFunnel extends () => Promise<{ funnel?: infer F }> ? NonNullable<F> : never,
  modeConfig: ReturnType<typeof getModeConfiguration>
): Promise<void> {
  // Render mode banner (floating, appends to body)
  if (modeConfig.showModeBanner) {
    renderModeBanner();
  }

  // Show Age Gate if required
  if (shouldShowAgeGate(funnel.showAgeGate)) {
    try {
      await showAgeGate();
    } catch {
      // User denied age verification - stop here
      logger.info('Age verification denied, stopping app');
      return;
    }
  }

  // Show GDPR consent if required
  if (shouldShowGDPRConsent(funnel.showGDPRConsent)) {
    const consent = await showGDPRConsentModal();
    logger.info('GDPR consent received', { type: consent.type });
  }

  // Build app shell
  container.innerHTML = `<div class="quiz-app" id="quiz-app"></div>`;

  const quizApp = document.getElementById('quiz-app')!;

  // Initialize components
  initQuizRenderer(quizApp);
  initContactForm(quizApp, funnel.finalPage, {
    funnelID: funnel.funnelID,
    version: funnel.version
  });

  // Render quiz
  renderQuiz(funnel);

  // Listen for quiz completion
  window.addEventListener('quiz:complete', () => {
    logger.info('Quiz complete event received');
    sendComplete(); // Notify parent iframe
    renderContactForm();
    setTimeout(sendResize, 100); // Resize after form renders
  });

  logger.info('App rendered');
}

/**
 * Apply theme from funnel config
 */
function applyTheme(theme: typeof loadFunnel extends () => Promise<{ funnel?: { theme?: infer T } }> ? T : never): void {
  if (!theme) return;

  const root = document.documentElement;

  const themeMap: Record<string, string> = {
    primaryColor: '--color-primary',
    primaryDark: '--color-primary-dark',
    primaryLight: '--color-primary-light',
    accentColor: '--color-accent',
    accentDark: '--color-accent-dark',
    accentLight: '--color-accent-light',
    gradientStart: '--gradient-start',
    gradientEnd: '--gradient-end',
    textPrimary: '--color-text-primary',
    textBody: '--color-text-body',
    textSecondary: '--color-text-secondary',
    backgroundColor: '--color-bg-page',
    borderRadius: '--radius-lg'
  };

  for (const [key, cssVar] of Object.entries(themeMap)) {
    const value = theme[key as keyof typeof theme];
    if (value) {
      root.style.setProperty(cssVar, value as string);
    }
  }

  // Apply custom fonts
  if (theme.fontFamily) {
    loadGoogleFont(theme.fontFamily);
    root.style.setProperty('--font-family-base', `'${theme.fontFamily}', var(--font-system)`);
  }

  if (theme.headingFontFamily) {
    loadGoogleFont(theme.headingFontFamily);
    root.style.setProperty('--font-family-heading', `'${theme.headingFontFamily}', var(--font-system)`);
  }

  logger.debug('Theme applied', theme);
}

/**
 * Load a Google Font dynamically
 */
function loadGoogleFont(fontFamily: string): void {
  // Skip if it's a system font or already loaded
  const systemFonts = ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana', 'system-ui'];
  if (systemFonts.some(f => fontFamily.toLowerCase().includes(f.toLowerCase()))) {
    return;
  }

  // Check if already loaded
  const existingLink = document.querySelector(`link[data-font="${fontFamily}"]`);
  if (existingLink) return;

  // Create Google Fonts link
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@400;500;600;700&display=swap`;
  link.setAttribute('data-font', fontFamily);
  document.head.appendChild(link);

  logger.debug('Loading Google Font', { fontFamily });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
